import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CardContent from "@material-ui/core/CardContent";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import Select from "react-select";
import LinearProgress from "@material-ui/core/LinearProgress";
import { fetchWithToken } from "../../../../apiUtils";

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
  },
  card: {
    marginTop: 40,
  },
  button: {
    margin: "5px",
  },
  checkbox: {
    width: "20px",
    height: "20px",
  },
  loading: {
    marginTop: "25%",
    marginLeft: "50%",
  },
});

const NewPool = () => {
  const classes = useStyles();

  const [formData, setFormData] = useState({ loading: true });

  useEffect(() => {
    fetchWithToken("api/Forms")
      .then((response) => response.json())
      .then((data) =>
        setFormData({
          sitesWithoutPools: data.sitesWithoutPools,
          primarySites: data.primarySites,
          loading: false,
        })
      );
  }, []);

  return (
    <>
      {formData.loading ? (
        <CircularProgress className={classes.loading} />
      ) : (
        <PoolForm
          primarySites={formData.primarySites}
          sitesWithoutPools={formData.sitesWithoutPools}
        />
      )}
    </>
  );
};

const PoolForm = ({ primarySites, sitesWithoutPools }) => {
  const classes = useStyles();

  const [ownerTech, setOwnerTech] = useState("");
  const [poolName, setPoolName] = useState("");
  const [poolDescription, setPoolDescription] = useState("");
  const [poolSite, setPoolSite] = useState(""); // stores the selected site id (string)
  const [poolHealthy, setPoolHealthy] = useState(false);
  const [teamsSites, setTeamsSites] = useState(sitesWithoutPools || []);
  const [loading, setLoading] = useState(false);
  const [successFetch, setSuccessFetch] = useState(false);

  const [alertMessage, setAlertMessage] = useState({
    message: "",
    varient: "",
    active: false,
  });

  const ownerTechnologyoptions = [
    { value: "IPT", label: "IPT" },
    { value: "Teams", label: "Teams" },
  ];

  const sendAlert = (message, varient) =>
    setAlertMessage({ message, varient, active: true });

  const clearAlert = () =>
    setAlertMessage({ message: "", varient: "", active: false });

  const getPrimarySites = () =>
    primarySites.map((site) => ({
      value: site.id,
      label: `${site.id} - ${site.name}`,
    }));

  const getSitesWithoutPools = () =>
    teamsSites.map((site) => ({
      value: site.id,
      label: `${site.id} - ${site.name}`,
    }));

  // Controlled value for ownerTech select
  const ownerTechValue = ownerTech
    ? { value: ownerTech, label: ownerTech }
    : null;

  // Controlled value for site select: find label from options arrays
  const siteValue = (() => {
    const allOptions =
      ownerTech === "Teams" ? getSitesWithoutPools() : getPrimarySites();
    return poolSite
      ? allOptions.find((o) => o.value === poolSite) || null
      : null;
  })();

  const clearPoolForm = () => {
    setPoolName("");
    setPoolSite("");
    setPoolHealthy(false);
    setPoolDescription("");
    clearAlert();
  };

  useEffect(() => {
    if (successFetch) {
      clearPoolForm();
      setSuccessFetch(false);
    }
  }, [successFetch]);

  const poolSubmitHandler = async (e) => {
    e.preventDefault();
    clearAlert();

    const payload = {
      name: poolName.trim(),
      description: poolDescription,
      siteId: poolSite,
      ownerTechnology: ownerTech ? ownerTech.toUpperCase() : "",
      healthy: poolHealthy ? "Y" : "N",
    };

    const missing = [];
    if (!payload.name) missing.push("Name");
    if (!payload.description) missing.push("Description");
    if (!payload.siteId) missing.push("Site");
    if (!payload.ownerTechnology) missing.push("Owner Technology");

    if (missing.length) {
      sendAlert(`Validation Error - ${missing.join(", ")} Required`, "warning");
      return;
    }

    setLoading(true);
    try {
      const res = await fetchWithToken(`api/Pools/`, "POST", payload);
      const data = await res.json();

      if (res.ok) {
        // update available Teams sites from server (data.returnObject expected)
        if (data.returnObject) setTeamsSites(data.returnObject);
        setSuccessFetch(true); // trigger clearing effect
        sendAlert(data.returnMessage || "Saved successfully!", "success");
      } else {
        sendAlert(data.returnMessage || "Error saving data", "error");
      }
    } catch (err) {
      sendAlert("Error saving data", "error");
    } finally {
      setLoading(false);
    }
  };

  const ownerChangeHandler = (selection) => {
    if (ownerTech) clearPoolForm();
    setOwnerTech(selection);
  };

  return (
    <div className="row">
      <div className="col-md-6 offset-md-3">
        <Card className={classes.card}>
          {alertMessage.active && (
            <Alert severity={alertMessage.varient} onClose={() => clearAlert()}>
              {alertMessage.message}
            </Alert>
          )}
          <CardContent>
            <div className="mb-3 row">
              <div className="col-sm-12">
                <Select
                  placeholder="Select Pool Type"
                  options={ownerTechnologyoptions}
                  onChange={(sel) => ownerChangeHandler(sel?.value || "")}
                  value={ownerTechValue}
                  menuPortalTarget={document.querySelector("body")}
                />
              </div>
            </div>

            {ownerTech && (
              <>
                <div className="mb-3 row">
                  <div className="col-sm-12">
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder={`${ownerTech} Pool Name *`}
                      maxLength="200"
                      value={poolName}
                      onChange={(e) => {
                        clearAlert();
                        setPoolName(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-sm-12">
                    <input
                      type="text"
                      className="form-control"
                      name="description"
                      placeholder={`${ownerTech} Pool Description *`}
                      maxLength="200"
                      value={poolDescription}
                      onChange={(e) => {
                        clearAlert();
                        setPoolDescription(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-sm-12">
                    <Select
                      name="siteId"
                      isClearable
                      placeholder={`Select Site For ${ownerTech} Pool *`}
                      options={
                        ownerTech === "Teams"
                          ? getSitesWithoutPools()
                          : getPrimarySites()
                      }
                      onChange={(sel) => {
                        clearAlert();
                        setPoolSite(sel?.value || "");
                      }}
                      value={siteValue}
                      menuPortalTarget={document.querySelector("body")}
                    />
                  </div>
                </div>

                <div className="mb-3 row align-items-center">
                  <div className="col-sm-2" style={{ marginLeft: "10px" }}>
                    <h6>Healthy:</h6>
                  </div>
                  <div className="col-sm-1">
                    <input
                      type="checkbox"
                      className={classes.checkbox}
                      checked={poolHealthy}
                      onChange={(e) => {
                        clearAlert();
                        setPoolHealthy(e.target.checked);
                      }}
                    />
                  </div>
                </div>

                {loading && (
                  <div style={{ margin: "25px" }}>
                    <span style={{ display: "table", margin: "0 auto" }}>
                      Please Wait For Process to Finish
                    </span>
                    <LinearProgress />
                  </div>
                )}

                <Button
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  onClick={poolSubmitHandler}
                >
                  Submit
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewPool;
